import { STEPS, type SubtitleMode } from '@engpod/core';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router';
import { Icon, type IconName } from '../components/Icon';
import { PageHead } from '../components/PageHead';
import { absolute, api, errorText, token } from '../lib/api';
import { useAuth } from '../lib/auth';
import { prefs } from '../lib/local';
import type { VocabItem } from '../types';

interface Stats {
  today: string;
  streak: number;
  studyDays: number;
  calendar: { day: string; actions: number }[];
  episodesCompleted: number;
  episodesStarted: number;
  vocab: { total: number; mastered: number; due: number };
  reviews: { total: number; today: number };
  recent: { episodeId: string; title: string; programTitle: string; publishedOn: string | null; posterUrl: string | null; stepsDone: number }[];
}

const VERSION = '0.1.0';

export function MePage() {
  const { me, refresh } = useAuth();
  const [stats, setStats] = useState<Stats | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const load = useCallback(() => api<Stats>('/me/stats').then(setStats, () => setStats(null)), []);
  useEffect(() => {
    if (me?.user) void load();
  }, [me?.user, load]);
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2200);
    return () => clearTimeout(t);
  }, [toast]);

  if (!me) return <p className="page muted">加载中…</p>;
  if (!me.user) return <LoginView />;

  return (
    <div className="page me">
      <PageHead eyebrow="MY ENGLISH JOURNEY" title="我的。" />
      <ProfileCard stats={stats} onSaved={refresh} />
      <TodayCard stats={stats} />
      <CalendarCard stats={stats} />
      {stats && stats.recent.length > 0 && <RecentCard stats={stats} />}
      <MemberCard />
      <SettingsGroup onToast={setToast} />
      <DataGroup onToast={setToast} />
      <AboutGroup />
      <AccountGroup />
      <p className="me-footer muted">
        ENGPOD {VERSION} · {me.localMode ? '本机测试版' : '测试版'}
      </p>
      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}

// --- profile & numbers ---------------------------------------------------------

function ProfileCard({ stats, onSaved }: { stats: Stats | null; onSaved(): void }) {
  const { me } = useAuth();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(me?.user?.display_name ?? '');
  const user = me!.user!;
  const display = user.display_name || (me?.localMode ? '学习者' : user.email.split('@')[0]!);
  const save = async () => {
    await api('/me', { method: 'PATCH', json: { displayName: name } });
    setEditing(false);
    onSaved();
  };
  return (
    <section className="profile-card">
      <div className="profile-top">
        <div className="avatar">{display.slice(0, 1).toUpperCase()}</div>
        <div className="profile-name">
          {editing ? (
            <form
              className="row tight"
              onSubmit={(e) => {
                e.preventDefault();
                void save();
              }}
            >
              <input autoFocus value={name} maxLength={40} onChange={(e) => setName(e.target.value)} placeholder="你的昵称" />
              <button className="small">保存</button>
            </form>
          ) : (
            <button className="plain name-button" onClick={() => setEditing(true)}>
              {display} <span>✎</span>
            </button>
          )}
          <small>{me?.localMode ? '本机自用模式 · 无需登录' : user.email}</small>
        </div>
        <span className="plan-pill">{me?.plan === 'member' ? '会员' : '免费版'}</span>
      </div>
      <div className="profile-stats">
        <Stat value={stats?.streak ?? 0} unit="天" label="连续学习" />
        <Stat value={stats?.episodesCompleted ?? 0} unit="集" label="完整学完" />
        <Stat value={stats?.vocab.total ?? 0} unit="个" label="生词与表达" />
      </div>
    </section>
  );
}

function Stat({ value, unit, label }: { value: number; unit: string; label: string }) {
  return (
    <div>
      <b>
        {value}
        <small>{unit}</small>
      </b>
      <span>{label}</span>
    </div>
  );
}

function TodayCard({ stats }: { stats: Stats | null }) {
  const goal = prefs.get<number>('reviewGoal', 20);
  const done = stats?.reviews.today ?? 0;
  const due = stats?.vocab.due ?? 0;
  const pct = Math.min(1, done / Math.max(1, goal));
  const r = 30;
  const c = 2 * Math.PI * r;
  return (
    <section className="me-card today-card">
      <svg className="ring" viewBox="0 0 76 76" aria-hidden="true">
        <circle cx="38" cy="38" r={r} className="ring-bg" />
        <circle cx="38" cy="38" r={r} className="ring-fg" strokeDasharray={`${c * pct} ${c}`} />
      </svg>
      <div className="today-text">
        <h3>今日目标</h3>
        <p>
          已复习 <b>{done}</b> / {goal} 条{due > 0 ? ` · 还有 ${due} 条待复习` : ''}
        </p>
        <small className="muted">{pct >= 1 ? '今天的目标完成了 🎉' : '复习时会播放单词所在的原句'}</small>
      </div>
      <Link className="button primary small" to="/review">
        去复习
      </Link>
    </section>
  );
}

/** 12 weeks × 7 days, newest column on the right; colour depth = number of learning actions that day. */
function CalendarCard({ stats }: { stats: Stats | null }) {
  const cells = useMemo(() => {
    const today = stats?.today ?? new Date().toISOString().slice(0, 10);
    const counts = new Map((stats?.calendar ?? []).map((d) => [d.day, d.actions]));
    const end = new Date(`${today}T00:00:00Z`);
    const lastSunday = new Date(end.getTime() + (6 - end.getUTCDay()) * 86_400_000); // fill the current week
    return Array.from({ length: 84 }, (_, i) => {
      const d = new Date(lastSunday.getTime() - (83 - i) * 86_400_000).toISOString().slice(0, 10);
      const n = counts.get(d) ?? 0;
      return { d, level: d > today ? -1 : n === 0 ? 0 : n < 4 ? 1 : n < 10 ? 2 : 3 };
    });
  }, [stats]);
  const thisWeek = cells.slice(-7).filter((c) => c.level > 0).length;
  return (
    <section className="me-card">
      <div className="row spread">
        <h3>学习日历</h3>
        <small className="muted">
          本周 {thisWeek} 天 · 累计 {stats?.studyDays ?? 0} 天
        </small>
      </div>
      <div className="calendar" role="img" aria-label={`最近 12 周学习 ${cells.filter((c) => c.level > 0).length} 天`}>
        {cells.map((c) => (
          <i key={c.d} className={`lv${c.level}`} title={c.d} />
        ))}
      </div>
      <div className="calendar-legend muted">
        <span>12 周前</span>
        <span className="row tight">
          少 <i className="lv1" />
          <i className="lv2" />
          <i className="lv3" /> 多
        </span>
      </div>
    </section>
  );
}

function RecentCard({ stats }: { stats: Stats }) {
  return (
    <section className="me-card">
      <h3>继续学习</h3>
      {stats.recent.map((r) => (
        <Link key={r.episodeId} className="recent-row" to={`/?ep=${r.episodeId}`}>
          {r.posterUrl ? <img src={absolute(r.posterUrl)} alt="" /> : <div className="recent-cover" />}
          <div>
            <b>{r.title}</b>
            <small className="muted">
              {r.programTitle}
              {r.publishedOn ? ` · ${r.publishedOn.slice(5)}` : ''}
            </small>
            <div className="steps-mini" aria-label={`已完成 ${r.stepsDone} / 5 步`}>
              {STEPS.map((s, i) => (
                <i key={s.id} className={i < r.stepsDone ? 'on' : ''} />
              ))}
              <small>{r.stepsDone}/5</small>
            </div>
          </div>
          <span className="chev">›</span>
        </Link>
      ))}
    </section>
  );
}

function MemberCard() {
  const { me } = useAuth();
  const [plans, setPlans] = useState<{ id: string; title: string; priceCny: number }[]>([]);
  useEffect(() => {
    api<{ plans: typeof plans }>('/billing/plans').then((d) => setPlans(d.plans), () => undefined);
  }, []);
  const member = me?.plan === 'member';
  return (
    <section className={`member-card${member ? ' active' : ''}`}>
      <div>
        <span className="eyebrow light">ENGPOD MEMBER</span>
        <h3>{member ? (me?.localMode ? '自用模式 · 全部功能已解锁' : '会员 · 全部功能已解锁') : '升级会员'}</h3>
        <ul>
          <li>全部往期节目</li>
          <li>生词本不限数量</li>
          <li>资料库每月 10 小时 AI 字幕与翻译</li>
        </ul>
      </div>
      {!member && (
        <div className="member-plans">
          {plans.map((p) => (
            <button key={p.id} disabled>
              {p.title} <b>¥{p.priceCny}</b>
            </button>
          ))}
          <small>支付即将开放</small>
        </div>
      )}
    </section>
  );
}

// --- grouped lists ------------------------------------------------------------------

function Group({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="me-group">
      <h4>{title}</h4>
      <div className="group-body">{children}</div>
    </section>
  );
}

function Row({ icon, label, hint, children, onClick, to, danger }: { icon: IconName; label: string; hint?: string; children?: ReactNode; onClick?(): void; to?: string; danger?: boolean }) {
  const content = (
    <>
      <span className="row-icon">
        <Icon name={icon} />
      </span>
      <span className="row-label">
        {label}
        {hint && <small>{hint}</small>}
      </span>
      {children ?? ((onClick || to) && <span className="chev">›</span>)}
    </>
  );
  const cls = `me-row${danger ? ' danger' : ''}`;
  if (to) {
    return (
      <Link className={cls} to={to}>
        {content}
      </Link>
    );
  }
  if (onClick) {
    return (
      <button className={cls} onClick={onClick}>
        {content}
      </button>
    );
  }
  return <div className={cls}>{content}</div>;
}

function Choice<T extends string | number>({ value, options, onChange }: { value: T; options: [T, string][]; onChange(v: T): void }) {
  return (
    <div className="mini-seg">
      {options.map(([v, label]) => (
        <button key={String(v)} className={v === value ? 'active' : ''} onClick={() => onChange(v)}>
          {label}
        </button>
      ))}
    </div>
  );
}

function SettingsGroup({ onToast }: { onToast(t: string): void }) {
  const [subtitle, setSubtitle] = useState(() => prefs.get<SubtitleMode>('subtitle', 'both'));
  const [rate, setRate] = useState(() => prefs.get<number>('rate', 1));
  const [follow, setFollow] = useState(() => prefs.get<boolean>('follow', false));
  const [goal, setGoal] = useState(() => prefs.get<number>('reviewGoal', 20));
  const [home, setHome] = useState(() => prefs.get<string>('homeProgram', 'bbc-easy'));
  const set = <T,>(key: string, setter: (v: T) => void) => (v: T) => {
    setter(v);
    prefs.set(key, v);
    onToast('已保存');
  };
  return (
    <Group title="学习设置">
      <Row icon="headphones" label="默认节目" hint="打开 App 时显示该节目的最新一期；没学完的那集优先">
        <Choice<string> value={home} options={[['bbc-easy', '入门 A1–A2'], ['bbc-6min', '中级 B1']]} onChange={set('homeProgram', setHome)} />
      </Row>
      <Row icon="quote" label="视频字幕">
        <Choice<SubtitleMode> value={subtitle} options={[['en', '英'], ['both', '中英'], ['zh', '中'], ['none', '无']]} onChange={set('subtitle', setSubtitle)} />
      </Row>
      <Row icon="play" label="默认语速">
        <Choice<number> value={rate} options={[[0.75, '0.75'], [0.85, '0.85'], [1, '1×'], [1.25, '1.25']]} onChange={set('rate', setRate)} />
      </Row>
      <Row icon="repeat" label="字幕列表跟随播放" hint="关闭时播放不会自动滚动页面">
        <button className={`switch${follow ? ' on' : ''}`} role="switch" aria-checked={follow} onClick={() => set('follow', setFollow)(!follow)}>
          <i />
        </button>
      </Row>
      <Row icon="clipboard" label="每日复习目标">
        <Choice<number> value={goal} options={[[10, '10'], [20, '20'], [30, '30'], [50, '50']]} onChange={set('reviewGoal', setGoal)} />
      </Row>
    </Group>
  );
}

function DataGroup({ onToast }: { onToast(t: string): void }) {
  const exportVocab = async () => {
    try {
      const { items } = await api<{ items: VocabItem[] }>('/vocab');
      if (!items.length) return onToast('生词本还是空的');
      const esc = (v: string | null | undefined) => `"${(v ?? '').replace(/"/g, '""')}"`;
      const lines = [['单词/表达', '类型', '释义', '备注', '原句', '原句中文', '节目', '日期', '单集'].join(',')];
      for (const it of items) {
        for (const c of it.contexts) {
          lines.push([it.surface, it.kind === 'word' ? '单词' : '表达', it.meaning, it.note, c.en, c.zh, c.programTitle ?? '我的资料', c.publishedOn, c.episodeTitle].map(esc).join(','));
        }
      }
      const file = new File(['﻿' + lines.join('\n')], `ENGPOD-生词本-${new Date().toISOString().slice(0, 10)}.csv`, { type: 'text/csv' });
      if (navigator.canShare?.({ files: [file] })) await navigator.share({ files: [file], title: 'ENGPOD 生词本' });
      else {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(file);
        a.download = file.name;
        a.click();
        URL.revokeObjectURL(a.href);
      }
      onToast(`已导出 ${items.length} 条，含原句和出处`);
    } catch (e) {
      if ((e as Error).name !== 'AbortError') onToast(errorText(e));
    }
  };
  const clearCache = () => {
    if (!confirm('清除本机缓存？会重置播放位置和显示设置，生词本和学习进度保存在服务器，不受影响。')) return;
    Object.keys(localStorage)
      .filter((k) => k.startsWith('engpod.') && k !== 'engpod.token')
      .forEach((k) => localStorage.removeItem(k));
    location.reload();
  };
  return (
    <Group title="数据">
      <Row icon="download" label="导出生词本" hint="CSV，每条带原句、中文和出处" onClick={exportVocab} />
      <Row icon="trash" label="清除本机缓存" hint="不影响生词本和学习进度" onClick={clearCache} />
    </Group>
  );
}

function AboutGroup() {
  const [open, setOpen] = useState<string | null>(null);
  const toggle = (id: string) => setOpen(open === id ? null : id);
  return (
    <Group title="关于">
      <Row icon="headphones" label="五步精听法" onClick={() => toggle('method')} />
      {open === 'method' && (
        <ol className="about-text">
          {STEPS.map((s) => (
            <li key={s.id}>
              <b>{s.label}</b>：{s.hint}
            </li>
          ))}
          <li>第二天起按 1 / 3 / 7 / 14 / 30 / 60 天复习生词，每次播放原句。</li>
        </ol>
      )}
      <Row icon="book" label="内容来源与版权" onClick={() => toggle('source')} />
      {open === 'source' && (
        <p className="about-text">
          6 Minute English、Real Easy English 的视频、音频和英文字幕版权归 BBC 所有。ENGPOD 是独立的学习工具，与 BBC 无关联，每集都链接回 BBC Learning English 原页面。中文译文、核心表达和练习为辅助编写，可能有误。
        </p>
      )}
      <Row icon="user" label="隐私说明" onClick={() => toggle('privacy')} />
      {open === 'privacy' && (
        <p className="about-text">
          我们只保存学习需要的数据：账号邮箱、学习进度、生词本和复习记录、你上传的资料。跟读录音只保存在本机当前页面，不会上传。你可以随时导出生词本或注销账号，注销后数据一并删除。
        </p>
      )}
    </Group>
  );
}

function AccountGroup() {
  const { me, signOut, refresh } = useAuth();
  const navigate = useNavigate();
  const remove = async () => {
    if (!confirm('注销后，你的学习进度、生词本、复习记录和上传的资料都会被永久删除，且无法恢复。确定注销？')) return;
    if (prompt('请输入「注销」确认') !== '注销') return;
    try {
      await api('/me', { method: 'DELETE' });
      token.set(null);
      await refresh();
      navigate('/');
    } catch (e) {
      alert(errorText(e));
    }
  };
  return (
    <Group title="账号">
      {me?.user?.role === 'admin' && <Row icon="settings" label="内容审核后台" hint="校订字幕、发布新一期" to="/admin" />}
      {me?.localMode ? (
        <Row icon="user" label="本机自用模式" hint="上线后改为登录使用，学习记录会保留" />
      ) : (
        <>
          <Row icon="close" label="退出登录" onClick={signOut} />
          <Row icon="trash" label="注销账号" hint="永久删除账号和全部学习数据" onClick={remove} danger />
        </>
      )}
    </Group>
  );
}

// --- login (only when not in local mode) ---------------------------------------------------

function LoginView() {
  const { me, signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [key, setKey] = useState('');
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [msg, setMsg] = useState<string | null>(null);
  const keyLogin = me?.login?.key ?? false;
  const emailLogin = me?.login?.email ?? !keyLogin;
  return (
    <div className="page me">
      <PageHead eyebrow="MY ENGLISH JOURNEY" title="登录。" />
      <section className="profile-card login">
        <h2>欢迎回来</h2>
        <p>{keyLogin ? '输入你的个人口令，登录一次后会一直保持登录。' : '用邮箱验证码登录，首次登录自动注册。'}学习进度和生词本会在网页和 App 之间同步。</p>
        {keyLogin && (
          <form
            className="stack"
            onSubmit={async (e) => {
              e.preventDefault();
              setMsg(null);
              try {
                const r = await api<{ token: string }>('/auth/key', { method: 'POST', json: { key } });
                await signIn(r.token);
              } catch (err) {
                setMsg(errorText(err));
              }
            }}
          >
            <input type="password" required value={key} onChange={(e) => setKey(e.target.value)} placeholder="个人口令" autoComplete="current-password" />
            <button className="primary">登录</button>
          </form>
        )}
        {emailLogin &&
          (step === 'email' ? (
            <form
              className="stack"
              onSubmit={async (e) => {
                e.preventDefault();
                setMsg(null);
                try {
                  const r = await api<{ sent: boolean; devCode?: string; devCodeInTerminal?: boolean }>('/auth/request-code', { method: 'POST', json: { email } });
                  setStep('code');
                  if (r.devCode) {
                    setCode(r.devCode);
                    setMsg(`开发模式：验证码 ${r.devCode}`);
                  } else if (r.devCodeInTerminal) setMsg('开发模式：验证码显示在电脑的服务器终端窗口里');
                  else setMsg('验证码已发送到邮箱，10 分钟内有效');
                } catch (err) {
                  setMsg(errorText(err));
                }
              }}
            >
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="邮箱" autoComplete="email" />
              <button className={keyLogin ? '' : 'primary'}>获取邮箱验证码</button>
            </form>
          ) : (
            <form
              className="stack"
              onSubmit={async (e) => {
                e.preventDefault();
                try {
                  const r = await api<{ token: string }>('/auth/verify', { method: 'POST', json: { email, code } });
                  await signIn(r.token);
                } catch (err) {
                  setMsg(errorText(err));
                }
              }}
            >
              <input inputMode="numeric" pattern="\d{6}" required value={code} onChange={(e) => setCode(e.target.value)} placeholder="6 位验证码" autoComplete="one-time-code" />
              <button className="primary">登录</button>
              <button type="button" className="plain" onClick={() => setStep('email')}>
                换个邮箱
              </button>
            </form>
          ))}
        {msg && <p className="login-msg">{msg}</p>}
      </section>
      <AboutGroup />
    </div>
  );
}
